import React, { useState, useEffect, useMemo, useCallback, Component } from 'react';
import DashboardErrorBoundary from '../components/common/DashboardErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import { useFiscalPeriod } from '../contexts/FiscalPeriodContext';
import {
  getCombinedDashboard,
  getSuperAdminDashboard,
  getAdminDashboard,
  getStaffDashboard,
  getActivityFeed,
  getQuickActions
} from '../api/dashboard';
import {
  ShoppingCart,
  Package,
  Users,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Box,
  Calendar,
  Wallet,
  Receipt,
  RefreshCw
} from 'lucide-react';

// Components
import KPICard from '../components/dashboard/KPICard';
import SalesTrendChart from '../components/dashboard/SalesTrendChart';
import RevenuePieChart from '../components/dashboard/RevenuePieChart';
import TopProductsChart from '../components/dashboard/TopProductsChart';
import QuickActions from '../components/dashboard/QuickActions';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import PeriodFilter from '../components/common/PeriodFilter';

// Utilities
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatters';

const DashboardInner = () => {
  console.log('[Dashboard] Component mounting/rendering...');
  const { user } = useAuth();
  console.log('[Dashboard] Auth user:', user ? { id: user.id, role: user.role, name: user.full_name } : 'null');
  const { periodRange, settings } = useFiscalPeriod();
  console.log('[Dashboard] Fiscal period:', { periodRange, settings: settings?.period_type });
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [quickActions, setQuickActions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState(null);

  // Local period filter state (null = use global, otherwise override)
  const [localPeriod, setLocalPeriod] = useState(null);

  // Current active date range (local override or global)
  const activeDateRange = useMemo(() => {
    if (localPeriod) {
      return {
        start_date: localPeriod.start_date || null,
        end_date: localPeriod.end_date || null,
        label: localPeriod.label
      };
    }
    return {
      start_date: periodRange?.start_date || null,
      end_date: periodRange?.end_date || null,
      label: periodRange?.period_label || 'All Time'
    };
  }, [localPeriod, periodRange]);

  // Handle period filter change
  const handlePeriodChange = useCallback((range) => {
    if (range.isGlobal) {
      setLocalPeriod(null);
    } else {
      setLocalPeriod(range);
    }
  }, []);

  // Fetch dashboard data based on user role
  const fetchDashboardData = useCallback(async () => {
    if (!user) {
      console.log('[Dashboard] No user found, skipping fetch');
      return;
    }

    try {
      console.log('[Dashboard] Starting fetch for user:', user.role, 'ID:', user.id);
      setLoading(true);
      setError(null);

      // Build date params from active date range
      const dateParams = {};
      if (activeDateRange.start_date) dateParams.start_date = activeDateRange.start_date;
      if (activeDateRange.end_date) dateParams.end_date = activeDateRange.end_date;
      console.log('[Dashboard] Date params:', dateParams);

      // Fetch all dashboard data in a single API call
      console.log('[Dashboard] Calling getCombinedDashboard API...');
      const response = await getCombinedDashboard(dateParams);
      console.log('[Dashboard] API response:', response);

      if (response.success) {
        console.log('[Dashboard] Setting dashboard data:', response.data.dashboard);
        setDashboardData(response.data.dashboard || {});
        setQuickActions(response.data.quickActions || []);
        setActivities(response.data.activityFeed || []);
      } else {
        console.error('[Dashboard] API returned success=false:', response);
        setError('Failed to load dashboard data. Please refresh the page.');
      }
    } catch (err) {
      console.error('[Dashboard] Error fetching dashboard data:', err);
      console.error('[Dashboard] Error details:', err.message, err.response);
      setError('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setLoading(false);
      console.log('[Dashboard] Fetch complete. Loading:', false);
    }
  }, [user, activeDateRange.start_date, activeDateRange.end_date]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'super_admin':
        return 'Super Administrator';
      case 'admin':
        return 'Administrator';
      case 'staff':
        return 'Staff Member';
      case 'branch_staff':
        return 'Branch Staff';
      case 'branch_admin':
        return 'Branch Admin';
      case 'retail_admin':
        return 'Retail Admin';
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-accent-gold text-primary-black';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'staff':
        return 'bg-green-100 text-green-800';
      case 'branch_staff':
        return 'bg-teal-100 text-teal-800';
      case 'branch_admin':
        return 'bg-indigo-100 text-indigo-800';
      case 'retail_admin':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Render Admin/Super Admin Dashboard
  const renderAdminDashboard = () => {
    // Always render the dashboard layout, use loading prop for cards
    const { overview, charts } = dashboardData || {};
    const { sales, customers, inventory, financial_health } = overview || {};

    return (
      <>
        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          <KPICard
            title="Total Revenue"
            value={formatCurrency(sales?.total_revenue || 0)}
            change={`Today: ${formatCurrency(sales?.today?.revenue || 0)}`}
            changeType="neutral"
            icon={DollarSign}
            iconColor="bg-accent-gold"
            loading={loading}
            error={error}
            sensitiveValue={true}
          />
          <KPICard
            title="Total Sales"
            value={formatNumber(sales?.total_sales_count || 0)}
            change={`Today: ${sales?.today?.sales_count || 0}`}
            changeType="neutral"
            icon={ShoppingCart}
            iconColor="bg-blue-500"
            loading={loading}
            error={error}
          />
          <KPICard
            title="Total Customers"
            value={formatNumber(customers?.total_customers || 0)}
            change={`New: ${customers?.new_customers_30_days || 0}`}
            changeType="positive"
            icon={Users}
            iconColor="bg-purple-500"
            loading={loading}
            error={error}
          />
          <KPICard
            title="Net Profit"
            value={formatCurrency(financial_health?.net_profit || 0)}
            change={formatPercentage(financial_health?.profit_margin || 0)}
            changeType={financial_health?.net_profit >= 0 ? 'positive' : 'negative'}
            valueColorType={financial_health?.net_profit >= 0 ? 'positive' : 'negative'}
            icon={TrendingUp}
            iconColor={financial_health?.net_profit >= 0 ? 'bg-green-500' : 'bg-red-500'}
            loading={loading}
            error={error}
            sensitiveValue={true}
          />
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          <KPICard
            title="Inventory Value"
            value={formatCurrency(inventory?.total_inventory_value || 0)}
            change={`${inventory?.total_products || 0} Products`}
            changeType="neutral"
            icon={Box}
            iconColor="bg-indigo-500"
            loading={loading}
            error={error}
            sensitiveValue={true}
          />
          <KPICard
            title="Low Stock Items"
            value={formatNumber(inventory?.low_stock_count || 0)}
            change="Action Required"
            changeType={inventory?.low_stock_count > 0 ? 'negative' : 'neutral'}
            icon={AlertTriangle}
            iconColor={inventory?.low_stock_count > 0 ? 'bg-orange-500' : 'bg-gray-400'}
            loading={loading}
            error={error}
          />
          <KPICard
            title="Gross Profit"
            value={formatCurrency(financial_health?.gross_profit || 0)}
            change={`Margin: ${financial_health?.gross_profit && sales?.total_revenue ? ((financial_health.gross_profit / sales.total_revenue) * 100).toFixed(1) : 0}%`}
            changeType="neutral"
            icon={TrendingUp}
            iconColor="bg-emerald-500"
            loading={loading}
            error={error}
            sensitiveValue={true}
          />
          <KPICard
            title="Total Expenses"
            value={formatCurrency(financial_health?.total_deductions || 0)}
            change={<>Expenses: {formatCurrency(financial_health?.total_expenses || 0)}<br/>Payroll: {formatCurrency(financial_health?.payroll_costs || 0)}</>}
            changeType="negative"
            icon={Wallet}
            iconColor="bg-orange-500"
            loading={loading}
            error={error}
            sensitiveValue={true}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <SalesTrendChart data={charts?.sales_trend || []} loading={loading} />
          <RevenuePieChart data={charts?.revenue_by_category || []} loading={loading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <TopProductsChart data={charts?.top_products || []} loading={loading} />
          <ActivityFeed activities={activities} loading={loading} />
        </div>

        {/* Quick Actions */}
        <QuickActions actions={quickActions} loading={loading} />
      </>
    );
  };

  // Render Staff Dashboard
  const renderStaffDashboard = () => {
    // Always render the dashboard layout, use loading prop for cards
    const { personal_stats, recent_sales } = dashboardData || {};
    const salesList = Array.isArray(recent_sales) ? recent_sales : [];

    return (
      <>
        {/* Personal KPI Cards - No monetary values for staff */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <KPICard
            title="My Total Sales"
            value={formatNumber(personal_stats?.my_sales_count || 0)}
            change={`Today: ${personal_stats?.today_sales || 0}`}
            changeType="neutral"
            icon={ShoppingCart}
            iconColor="bg-blue-500"
            loading={loading}
            error={error}
          />
          <KPICard
            title="Items Sold"
            value={formatNumber(personal_stats?.items_sold || personal_stats?.my_sales_count * 2 || 0)}
            change="Total units"
            changeType="neutral"
            icon={Package}
            iconColor="bg-purple-500"
            loading={loading}
            error={error}
          />
          <KPICard
            title="Customers Served"
            value={formatNumber(personal_stats?.customers_served || personal_stats?.my_sales_count || 0)}
            change="Unique customers"
            changeType="neutral"
            icon={Users}
            iconColor="bg-green-500"
            loading={loading}
            error={error}
          />
        </div>

        {/* Recent Sales Table - No Amount column for staff */}
        <div className="bg-theme-secondary rounded-lg shadow-theme-md p-4 md:p-6 mb-6 md:mb-8 border border-theme">
          <h3 className="text-base md:text-lg font-semibold text-theme-primary mb-4">My Recent Sales</h3>
          {salesList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-theme">
                <thead className="bg-theme-tertiary">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-theme-muted uppercase">
                      Receipt #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-theme-muted uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-theme-muted uppercase">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-theme-muted uppercase">
                      Items
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-theme-muted uppercase">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-theme-muted uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-theme-secondary divide-y divide-theme">
                  {salesList.map((sale) => (
                    <tr key={sale.id} className="hover:bg-theme-tertiary">
                      <td className="px-4 py-3 text-sm font-medium text-accent-gold">{sale.receipt_number}</td>
                      <td className="px-4 py-3 text-sm text-theme-secondary">
                        {new Date(sale.sale_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-theme-primary">{sale.customer_name || 'Walk-in'}</td>
                      <td className="px-4 py-3 text-sm text-theme-primary">{sale.item_count || '-'}</td>
                      <td className="px-4 py-3 text-sm text-theme-secondary capitalize">{sale.payment_method}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          sale.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : sale.payment_status === 'partial'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {sale.payment_status || 'Paid'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-theme-muted text-center py-8">No sales recorded yet</p>
          )}
        </div>

        {/* Activity Feed and Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <ActivityFeed activities={activities} loading={loading} title="My Recent Activity" />
          <QuickActions actions={quickActions} loading={loading} />
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-theme-secondary shadow-theme-sm mb-4 border-b border-theme">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-theme-primary">Dashboard</h1>
              <p className="mt-1 text-xs md:text-sm text-theme-secondary">
                Welcome back, {user?.full_name}!
                {localPeriod && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                    {activeDateRange.label}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Period Filter */}
              <PeriodFilter
                onChange={handlePeriodChange}
                showLabel={true}
                compact={false}
                storageKey="retail_dashboard"
              />
              <button
                onClick={fetchDashboardData}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-theme-secondary bg-theme-tertiary border border-theme rounded-lg hover:bg-theme-secondary"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <span
                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold ${getRoleBadgeColor(
                  user?.role
                )}`}
              >
                {getRoleDisplay(user?.role)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        {error && (
          <div className="bg-red-50 border-2 border-red-400 p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Render appropriate dashboard based on role */}
        {['staff', 'branch_staff'].includes(user?.role) ? renderStaffDashboard() : renderAdminDashboard()}
      </div>
    </div>
  );
};

// Wrap with ErrorBoundary to prevent blank page on render crashes
const Dashboard = () => (
  <DashboardErrorBoundary>
    <DashboardInner />
  </DashboardErrorBoundary>
);

export default Dashboard;
